import { TransactionFlow } from '@portal/models/enumerators';
import filterDuplicates from '~/utils/filterDuplicates';
import {
  getInstance,
  getCleanInstance,
} from '@portal/repositories/securitySdkConfig';

const BASE_URL = '/account';
const V2_BASE_URL = '/v2/account';
const V3_BASE_URL = '/v3/account';

interface SwitchAccountResponse {
  token: string;
  refreshToken: string;
}

const AccountApi = {
  getUserBalance: async (
    paymentAccountId?: string
  ): Promise<models.Balance> => {
    const instance = getInstance();
    const { data } = await instance.get<models.Balance>(
      `${V2_BASE_URL}/balance`,
      {
        headers: paymentAccountId
          ? {
              'payment-account-id': paymentAccountId,
            }
          : {},
      }
    );

    return data;
  },

  getExtract: async (
    flow?: TransactionFlow,
    params?: models.StatementSearchParams
  ): Promise<{ rows: bank.StatementItem[] }> => {
    const instance = getInstance();
    const { data } = await instance.get<{ rows: bank.StatementItem[] }>(
      `${V3_BASE_URL}/statement`,
      {
        params: {
          flow,
          ...params,
        },
      }
    );

    const { rows } = data;
    const transactionsData = {
      rows: filterDuplicates(rows || [], 'id'),
    };

    return transactionsData;
  },

  getIncomeReport: async (year: string) => {
    const instance = getInstance();
    const { data } = await instance.get(
      `${BASE_URL}/request-income-report?year=${year}`
    );
    return data;
  },

  requestClosure: async (comment?: string) => {
    const instance = getInstance();
    const { data } = await instance.post(`${BASE_URL}/request-closure`, {
      comment,
    });
    return data;
  },

  getExportExtractByEmail: async (
    startAt: string,
    endAt: string,
    order: 'asc' | 'desc',
    fileType: string
  ) => {
    const instance = getInstance();
    const { data } = await instance.get(
      `${BASE_URL}/export-statement?startAt=${startAt}&endAt=${endAt}&order=${order}&fileType=${fileType}`
    );
    return data;
  },

  getExportExtractToDownload: async (
    startAt: string,
    endAt: string,
    order: 'asc' | 'desc',
    fileType: string
  ) => {
    const instance = getInstance();
    const { data } = await instance.get(
      `${BASE_URL}/statement-download-url?startAt=${startAt}&endAt=${endAt}&order=${order}&fileType=${fileType}`
    );
    return data;
  },

  getProfilePhotoUploadUrl: async () => {
    const instance = getInstance();
    const { data } = await instance.get<models.OnboardingGetImageUrlResponse>(
      `${BASE_URL}/profile-photo`
    );

    return data;
  },

  putProfilePhoto: async (imageBody: Buffer, bucketUrl: string) => {
    const instance = getCleanInstance(bucketUrl);

    await instance.put(bucketUrl, imageBody);
  },
  getUserConfig: async (paymentAccountId?: string) => {
    const instance = getInstance();
    const { data } = await instance.get(`/account-settings/`, {
      headers: {
        'payment-account-id': paymentAccountId,
      },
    });

    return data;
  },
  putUserConfig: async (id: string, config: models.Config) => {
    const instance = getInstance();
    await instance.put(`/account-settings/${id}`, { value: config.value });
  },

  getInvestmentPosition: async (
    params?: advancedFilterModels.InvestmentPositionAdvancedFilter
  ) => {
    const instance = getInstance();
    const { data } = await instance.get('/account/investment-position', {
      params,
    });

    return data;
  },

  getOverdraftInfo: async (): Promise<models.OverdraftInfo> => {
    const instance = getInstance();
    const { data } = await instance.get<models.OverdraftInfo>(
      `${V3_BASE_URL}/overdraft-info`
    );

    return data;
  },

  switchAccount: async (id: string) => {
    const instance = getInstance();
    const { data } = await instance.patch<SwitchAccountResponse>(
      `${V3_BASE_URL}/switch/${id}`
    );
    return data;
  },

  myUsers: async () => {
    const instance = getInstance();
    const { data } = await instance.get(`${V3_BASE_URL}/my-users`);

    return data;
  },

  getOverdraftHash: async (): Promise<{
    hasOverdraftHash: boolean;
  }> => {
    const instance = getInstance();
    const { data } = await instance.get(`${V3_BASE_URL}/get-overdraft-hash`);
    return data?.hasOverdraftHash;
  },

  postOverdraftSignature: async (message: string) => {
    const instance = getInstance();
    const { data } = await instance.post(`${V3_BASE_URL}/overdraft-signature`, {
      message,
    });
    return data;
  },

  myUsersCount: async () => {
    const instance = getInstance();
    const { data } = await instance.get<{
      count: number;
    }>(`${V3_BASE_URL}/my-users/count`);

    return data.count;
  },

  getDailyBalance: async (
    params: advancedFilterModels.TransactionFilter
  ): Promise<models.DailyBalanceItem[]> => {
    const instance = getInstance();

    const formattedParams = { ...params };
    if (params.startAt) {
      const startDate = new Date(params.startAt);
      formattedParams.startAt = startDate.toLocaleDateString('pt-BR');
    }
    if (params.endAt) {
      const endDate = new Date(params.endAt);
      formattedParams.endAt = endDate.toLocaleDateString('pt-BR');
    }

    const { data } = await instance.get<models.DailyBalanceItem[]>(
      `${V3_BASE_URL}/daily-balance`,
      {
        params: formattedParams,
      }
    );

    return data;
  },
};

export default AccountApi;
